import { lazy } from 'react';
import type { ReactElement } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequireFeature } from '@/features/authentication';
import { LazyLoad } from '@/shared/components/LazyLoad';
import { TemplatesLanding } from './common/overview/components/TemplatesLanding';
import {
  prTemplateDefinition,
  rfqTemplateDefinition,
  supplierOnboardingTemplateDefinition,
} from './common/overview/models';
import type { TemplateDefinition } from './common/overview/models/templateDefinition.types';

/**
 * The Templates route table: one entry per kind, all rendering one page.
 *
 * Each is lazy so a kind's chunk is fetched only when its route is visited, and
 * the imports are written out rather than built from the registry in a loop -- a
 * bundler can only split on a literal `import()` path, so a computed one would
 * defeat the arrangement. The same reasoning as `features/masters/routes.tsx`.
 */
const TemplatesPage = lazy(() => import('./common/list/pages/TemplatesPage'));

/**
 * One kind's route: its own page, behind the FEATURE the RBAC matrix scores it against.
 *
 * The guard is here rather than in `app/routes` for the reason `features/masters/routes.tsx` gives: the
 * feature a route leads to is the same source the sidebar filters on and the same one the screen reads
 * for its buttons, so a hidden menu entry is also a closed URL rather than merely an unlisted one.
 */
function page(definition: TemplateDefinition, element: ReactElement): RouteObject['element'] {
  return (
    <RequireFeature feature={definition.feature}>
      <LazyLoad>{element}</LazyLoad>
    </RequireFeature>
  );
}

export const templateRoutes: RouteObject[] = [
  {
    path: 'templates',
    children: [
      /*
        `/templates` is not a page, and the landing is not a fixed redirect.

        It was `<Navigate to="/templates/rfq">` -- the reference's default kind. That sent every role to
        the RFQ template, so one granted only the PR template clicked Templates in its own sidebar and was
        refused by a screen it had never been given. `TemplatesLanding` goes to the first kind the role can
        actually read, in the menu's order, which for a role holding RFQ is still RFQ.
      */
      { index: true, element: <TemplatesLanding /> },
      {
        path: 'pr',
        element: page(prTemplateDefinition, <TemplatesPage key="pr" definition={prTemplateDefinition} />),
      },
      {
        path: 'rfq',
        element: page(rfqTemplateDefinition, <TemplatesPage key="rfq" definition={rfqTemplateDefinition} />),
      },
      {
        path: 'supplier-onboarding',
        element: page(
          supplierOnboardingTemplateDefinition,
          <TemplatesPage
            key="supplier-onboarding"
            definition={supplierOnboardingTemplateDefinition}
          />,
        ),
      },
    ],
  },
];
